import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { api, downloadBlob } from '../../api/client'
import type { PatchReport } from '../../api/client'
import { useAsync } from '../../hooks/useAsync'
import { localeCorrente, t } from '../../i18n/lingua'
import { CategoryBars, CountSeries, StackedCountSeries, StatusStrip } from '../charts'
import { Card, EmptyState, ErrorBanner, Spinner, StatCard, formatDateTime } from '../common'
import { DataTable } from '../DataTable'
import { EnvironmentTable } from './Overview'
import { ReportSchedules } from './ReportSchedules'
import { Evidence } from './Evidence'
import { chipClass, environmentLabel, patchKindLabel, platformLabel, reasonLabel, severityLabel } from './labels'

const PERIODS = [30, 90, 180, 365] as const

/**
 * The patch report of a period, on screen and to download: the executive
 * summary (a few pages with the charts and the posture in sentences) and the
 * full report (every table, also as Excel). What is shown here and what is
 * printed come from the same call, so they never disagree.
 */
export function Reports() {
  const [days, setDays] = useState<number>(30)
  const [environment, setEnvironment] = useState('')
  const state = useAsync(() => api.patchReport(days, environment), [days, environment])
  const d = state.data
  // The environments to choose from: kept from the last report, so choosing one does not empty the list.
  const [environments, setEnvironments] = useState<string[]>([])
  useEffect(() => {
    if (d && !d.environment) setEnvironments(d.environments)
  }, [d])

  return (
    <>
      <Card title={t('Report delle patch')} actions={<Downloads days={days} environment={environment} />}>
        <div className="patch-toolbar">
          <div className="segmented" role="group" aria-label={t('Periodo')}>
            {PERIODS.map((p) => (
              <button key={p} type="button" className={days === p ? 'segmented__on' : ''} onClick={() => setDays(p)}>
                {p === 365 ? t('Un anno') : t('{n} giorni', { n: p })}
              </button>
            ))}
          </div>
          <label className="field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <span className="field__label">{t('Ambiente')}</span>
            <select value={environment} onChange={(e) => setEnvironment(e.target.value)}>
              <option value="">{t('tutti')}</option>
              {(environment && !environments.includes(environment) ? [...environments, environment] : environments).map((e) => (
                <option key={e} value={e}>{environmentLabel(e)}</option>
              ))}
            </select>
          </label>
        </div>
        <p className="field__hint" style={{ marginTop: 0 }}>
          {t('«Sintesi direzionale»: poche pagine con i numeri, i grafici e la situazione scritta in frasi, per chi decide. «Report completo»: gli stessi grafici e poi tutte le tabelle — macchine, patch, patch per macchina, installazioni e interventi del periodo, macchine non aggiornabili, sistemi fuori supporto, rischi accettati ed eccezioni, tempo di correzione, copertura per macchina, riduzione del rischio, cicli delle politiche e ticket —, anche in Excel.')}
        </p>
        <ErrorBanner error={state.error} />
        {!d ? !state.error && <Spinner /> : <Kpis d={d} />}
      </Card>
      {d && <Charts d={d} />}
      <ReportSchedules environments={environments} />
      <Evidence />
    </>
  )
}

function Downloads({ days, environment }: { days: number; environment: string }) {
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<unknown>(null)
  async function get(kind: 'executive' | 'full', fmt: 'pdf' | 'xlsx') {
    const key = `${kind}-${fmt}`
    setBusy(key)
    setError(null)
    try {
      const blob = await downloadBlob(api.patchReportExportPath(kind, fmt, days, environment))
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${kind === 'executive' ? 'censo-patch-sintesi' : 'censo-patch-report'}.${fmt}`
      a.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      setError(e)
    } finally {
      setBusy(null)
    }
  }
  return (
    <>
      <button disabled={busy !== null} onClick={() => get('executive', 'pdf')}>
        {busy === 'executive-pdf' ? t('Preparazione…') : t('Sintesi direzionale (PDF)')}
      </button>
      <button className="secondary" disabled={busy !== null} onClick={() => get('full', 'pdf')}>
        {busy === 'full-pdf' ? t('Preparazione…') : t('Report completo (PDF)')}
      </button>
      <button className="secondary" disabled={busy !== null} onClick={() => get('full', 'xlsx')}>
        {busy === 'full-xlsx' ? t('Preparazione…') : t('Report completo (Excel)')}
      </button>
      <ErrorBanner error={error} />
    </>
  )
}

function Kpis({ d }: { d: PatchReport }) {
  const k = d.kpi
  const nf = (n: number) => n.toLocaleString(localeCorrente())
  return (
    <>
      <h3 className="patch-report__title">{t('Situazione oggi')}</h3>
      <div className="stats">
        <StatCard label={t('Macchine aggiornate')} value={`${k.up_to_date_pct}%`}
          hint={t('{n} su {m}', { n: k.up_to_date, m: k.hosts })}
          tone={k.up_to_date_pct >= 90 ? 'ok' : k.up_to_date_pct >= 60 ? 'warn' : 'error'} />
        <StatCard label={t('Entro i tempi (SLA)')} value={`${k.within_sla_pct}%`}
          hint={t('{n} su {m}', { n: k.within_sla, m: k.hosts })}
          tone={k.within_sla_pct >= 95 ? 'ok' : k.within_sla_pct >= 75 ? 'warn' : 'error'} />
        <StatCard label={t('Installazioni mancanti')} value={nf(k.missing)}
          hint={t('{n} patch diverse, {u} urgenti', { n: k.patches, u: k.urgent })} />
        <StatCard label={t('Sfruttate attivamente')} value={nf(k.exploited)} hint="CISA KEV"
          tone={k.exploited ? 'error' : 'ok'} />
        <StatCard label={t('Oltre i tempi')} value={nf(k.overdue)}
          hint={t('età mediana {n} giorni', { n: k.median_age_days })} tone={k.overdue ? 'warn' : 'ok'} />
        <StatCard label={t('Non aggiornabili')} value={nf(k.not_patchable)}
          hint={t('{e} fuori supporto, {r} da riavviare', { e: k.end_of_life, r: k.needs_reboot })}
          tone={k.not_patchable ? 'warn' : undefined} />
        <StatCard label={t('Rischio totale')} value={nf(k.risk_total)}
          hint={k.risk_change_pct == null
            ? t('{n} macchine a rischio urgente', { n: k.urgent_hosts })
            : t('{p}% nel periodo, {n} macchine a rischio urgente', { p: k.risk_change_pct > 0 ? `+${k.risk_change_pct}` : k.risk_change_pct, n: k.urgent_hosts })}
          tone={k.risk_change_pct == null ? undefined : k.risk_change_pct <= 0 ? 'ok' : 'warn'} />
      </div>
      <h3 className="patch-report__title">{t('Nel periodo')}</h3>
      <div className="stats">
        <StatCard label={t('Installazioni riuscite')} value={nf(k.installs_applied)}
          hint={t('{n} non riuscite', { n: k.installs_failed })} />
        <StatCard label={t('Successo')} value={`${k.success_pct}%`}
          tone={k.success_pct >= 95 ? 'ok' : k.success_pct >= 80 ? 'warn' : 'error'} />
        <StatCard label={t('Aggiornamenti installati')} value={nf(k.updates_installed)}
          hint={t('su {n} macchine', { n: k.machines_patched })} />
        <StatCard label={t('Verificate')} value={k.verified_pct === null ? '—' : `${k.verified_pct}%`}
          hint={t('patch non più mancanti dopo l’inventario')} />
        <StatCard label={t('Tempo medio di installazione')}
          value={k.mean_install_minutes ? t('{n} min', { n: k.mean_install_minutes }) : '—'} />
        <StatCard label={t('Interventi')} value={nf(k.jobs)} hint={t('{n} riavvii', { n: k.reboots })} />
      </div>
      <h3 className="patch-report__title">{t('Ciclo di vita e governo')}</h3>
      <div className="stats">
        <StatCard label={t('Copertura')} value={k.coverage_pct === null ? '—' : `${k.coverage_pct}%`}
          hint={t('{n} su {m} computer letti dall’interno', { n: k.covered, m: k.computers })}
          tone={k.coverage_pct === null ? undefined : k.coverage_pct >= 90 ? 'ok' : 'warn'} />
        <StatCard label={t('Patch mancanti nuove')} value={nf(k.new)}
          hint={k.reopened ? t('{n} tornate', { n: k.reopened }) : undefined} />
        <StatCard label={t('Patch mancanti corrette')} value={nf(k.fixed)}
          hint={k.fixed_within_sla_pct === null ? undefined : t('{n}% entro lo SLA', { n: k.fixed_within_sla_pct })} />
        <StatCard label={t('Tempo medio di correzione')} value={k.mttr_days === null ? '—' : t('{n} giorni', { n: k.mttr_days })}
          hint={k.mttr_median_days === null ? undefined : t('mediana {n} giorni', { n: k.mttr_median_days })} />
        <StatCard label={t('Eccezioni attive')} value={nf(k.exceptions_active)}
          hint={t('{n} da decidere, {s} in scadenza', { n: k.exceptions_requested, s: k.exceptions_expiring })}
          tone={k.exceptions_requested || k.exceptions_expiring ? 'warn' : undefined} />
        <StatCard label={t('Cicli delle politiche')} value={nf(k.policy_cycles)}
          hint={t('{n} fermati, {t} ticket aperti', { n: k.policy_cycles_stopped, t: k.tickets_opened })}
          tone={k.policy_cycles_stopped ? 'warn' : undefined} />
      </div>
    </>
  )
}

function Charts({ d }: { d: PatchReport }) {
  const days = d.period.days
  const sevSla = d.sla_days
  const maxAging = Math.max(1, ...d.aging.map((a) => a.total))
  return (
    <>
      <Card title={t('Andamento')}>
        {d.trend.length < 2 ? (
          <EmptyState title={t('L’andamento si costruisce da oggi: un punto al giorno.')} />
        ) : (
          <div className="patch-trend">
            <div>
              <h3 className="patch-trend__title">{t('Macchine da aggiornare')}</h3>
              <CountSeries points={d.trend.map((p) => ({ date: p.day, value: p.hosts - p.up_to_date }))}
                tone="warn" label={t('macchine da aggiornare')} altezza={170} />
            </div>
            <div>
              <h3 className="patch-trend__title">{t('Installazioni mancanti')}</h3>
              <CountSeries points={d.trend.map((p) => ({ date: p.day, value: p.missing }))}
                label={t('installazioni mancanti')} altezza={170} />
            </div>
            {d.trend.filter((p) => p.risk).length >= 2 && (
              <div>
                <h3 className="patch-trend__title">{t('Rischio totale')}</h3>
                <CountSeries points={d.trend.filter((p) => p.risk).map((p) => ({ date: p.day, value: p.risk }))}
                  tone="error" label={t('rischio totale')} altezza={170} />
              </div>
            )}
          </div>
        )}
      </Card>

      <Card title={t('Installazioni negli ultimi {n} giorni', { n: days })}>
        <StackedCountSeries
          points={d.activity.map((a) => ({ date: a.day, values: { applied: a.applied, needs_reboot: a.needs_reboot, failed: a.failed } }))}
          series={[
            { key: 'applied', label: t('installate'), color: 'var(--ok)' },
            { key: 'needs_reboot', label: t('da riavviare'), color: 'var(--warn)' },
            { key: 'failed', label: t('non riuscite'), color: 'var(--error)' },
          ]}
          label={t('installazioni')}
          emptyHint={t('Nessuna installazione nel periodo.')}
        />
      </Card>

      <div className="patch-report__grid">
        <Card title={t('Per gravità: entro e oltre i tempi')}>
          {d.by_severity.map((s) => (
            <div key={s.severity} className="patch-report__row">
              <div className="patch-report__rowhead">
                <strong>{severityLabel(s.severity)}</strong>
                <span className="text-muted">{t('entro {n} giorni', { n: sevSla[s.severity] ?? s.sla_days })}</span>
              </div>
              <StatusStrip emptyHint={t('nessuna mancante')} data={[
                { key: 'in', label: t('entro SLA'), value: s.within_sla, tone: 'ok' },
                { key: 'out', label: t('oltre SLA'), value: s.overdue, tone: 'error' },
              ]} />
            </div>
          ))}
        </Card>
        <Card title={t('Età delle patch mancanti')}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t('Giorni')}</th>
                  <th>{t('Critica')}</th>
                  <th>{t('Alta')}</th>
                  <th>{t('Media')}</th>
                  <th>{t('Bassa')}</th>
                  <th>{t('Totale')}</th>
                </tr>
              </thead>
              <tbody>
                {d.aging.map((a) => (
                  <tr key={a.bucket}>
                    <td className="mono">{a.bucket}</td>
                    <td>{a.critica || ''}</td>
                    <td>{a.alta || ''}</td>
                    <td>{a.media || ''}</td>
                    <td>{a.bassa || ''}</td>
                    <td>
                      <span className="patch-pct">
                        <span className="patch-pct__track"><span className="patch-pct__fill patch-pct__fill--warn" style={{ width: `${(a.total * 100) / maxAging}%` }} /></span>
                        <span className="patch-pct__value">{a.total}</span>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <Card title={t('Per ambiente')}>
        <EnvironmentTable rows={d.by_environment} envKey={d.environment_key} />
      </Card>

      <Lifecycle d={d} />

      <div className="patch-report__grid">
        <Card title={t('Patch mancanti per tipo')}>
          <CategoryBars data={d.by_kind.map((k) => ({ key: k.kind, label: patchKindLabel(k.kind), value: k.open }))}
            emptyHint={t('Nessuna patch mancante.')} />
        </Card>
        <Card title={t('Per sistema')}>
          <DataTable
            rows={d.by_platform}
            rowKey={(p) => p.platform || '-'}
            columns={[
              { key: 'platform', header: t('Sistema'), value: (p) => (p.platform ? platformLabel(p.platform) : t('non riconosciuto')) },
              { key: 'hosts', header: t('Macchine'), type: 'number', render: (p) => p.hosts },
              { key: 'up_to_date', header: t('Aggiornate'), type: 'number', render: (p) => p.up_to_date },
              { key: 'missing', header: t('Patch mancanti'), type: 'number', render: (p) => p.missing },
            ]}
          />
        </Card>
      </div>

      <div className="patch-report__grid">
        <Card title={t('Patch da installare per prime (macchine)')}>
          <CategoryBars data={d.top_patches.map((p) => ({ key: p.key, label: p.name, value: p.host_count }))}
            emptyHint={t('Nessuna patch mancante.')} />
        </Card>
        <Card title={t('Macchine più esposte (patch mancanti)')}>
          <CategoryBars data={d.top_hosts.map((h) => ({ key: h.ci_id, label: h.name, value: h.missing + h.software }))}
            emptyHint={t('Nessuna macchina da aggiornare.')} />
        </Card>
      </div>

      {(d.not_patchable.length > 0 || d.end_of_life_hosts.length > 0) && (
        <div className="patch-report__grid">
          <Card title={t('Non aggiornabili ({n})', { n: d.not_patchable.length })}>
            {d.not_patchable.length === 0 ? <p className="text-muted">{t('Nessuna.')}</p> : (
              <ul className="patch-reasons">
                {d.not_patchable.map((n) => (
                  <li key={n.ci_id}>
                    <Link to={`/cis/${n.ci_id}`}>{n.name}</Link> — {n.reasons.map((r) => reasonLabel(r, n.missing_tags)).join(', ')}
                  </li>
                ))}
              </ul>
            )}
          </Card>
          <Card title={t('Sistemi fuori supporto ({n})', { n: d.end_of_life_hosts.length })}>
            {d.end_of_life_hosts.length === 0 ? <p className="text-muted">{t('Nessuno.')}</p> : (
              <>
                <p className="field__hint" style={{ marginTop: 0 }}>{t('Nessuna patch li mette in sicurezza: va aggiornato il sistema operativo.')}</p>
                <ul className="patch-reasons">
                  {d.end_of_life_hosts.map((h) => (
                    <li key={h.ci_id}><Link to={`/cis/${h.ci_id}`}>{h.name}</Link>{h.os ? ` — ${h.os}` : ''}</li>
                  ))}
                </ul>
              </>
            )}
          </Card>
        </div>
      )}
    </>
  )
}

function runStatusLabel(s: string): string {
  switch (s) {
    case 'done': return t('concluso')
    case 'stopped': return t('fermato')
    default: return t('in corso')
  }
}

/** What the rest of Patching keeps, for the period: new against fixed, the time
 *  to remediate, the coverage, the patches that take away most risk, the policies. */
function Lifecycle({ d }: { d: PatchReport }) {
  const c = d.coverage
  return (
    <>
      <Card title={t('Patch mancanti nuove e corrette, settimana per settimana')}>
        <StackedCountSeries
          points={d.lifecycle.weeks.map((w) => ({ date: w.week, values: { detected: w.detected + w.reopened, fixed: w.fixed } }))}
          series={[{ key: 'detected', label: t('nuove'), color: 'var(--serie-1)' }, { key: 'fixed', label: t('corrette'), color: 'var(--ok)' }]}
          label={t('patch mancanti')}
          emptyHint={t('Nessuna patch mancante nuova o corretta nel periodo.')}
        />
      </Card>

      <div className="patch-report__grid">
        <Card title={t('Tempo di correzione per gravità')}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t('Gravità')}</th>
                  <th>{t('Corrette')}</th>
                  <th>{t('Media')}</th>
                  <th>{t('Mediana')}</th>
                  <th>{t('Entro lo SLA')}</th>
                </tr>
              </thead>
              <tbody>
                {['critica', 'alta', 'media', 'bassa', '*'].map((sev) => {
                  const m = d.lifecycle.mttr[sev]
                  return (
                    <tr key={sev}>
                      <td>{sev === '*' ? <strong>{t('Tutte')}</strong> : severityLabel(sev)}</td>
                      <td>{m?.count || ''}</td>
                      <td>{m?.mean != null ? t('{n} giorni', { n: m.mean }) : ''}</td>
                      <td>{m?.median != null ? t('{n} giorni', { n: m.median }) : ''}</td>
                      <td>{m?.count ? `${Math.round((100 * m.within_sla) / m.count)}%` : ''}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </Card>
        <Card title={t('Copertura')}>
          <StatusStrip emptyHint={t('Nessun computer.')} data={[
            { key: 'agent', label: t('con l’agente'), value: c.by_category.agent, tone: 'ok' },
            { key: 'credentials', label: t('con credenziali'), value: c.by_category.credentials, tone: 'ok' },
            { key: 'stale', label: t('lettura vecchia'), value: c.by_category.stale, tone: 'warn' },
            { key: 'network_only', label: t('solo dalla rete'), value: c.by_category.network_only, tone: 'error' },
            { key: 'unknown_os', label: t('sistema non riconosciuto'), value: c.by_category.unknown_os, tone: 'warn' },
          ]} />
          <p className="field__hint">{t('Le patch di chi è visto solo dalla rete o ha una lettura più vecchia di {n} giorni non sono valutate.', { n: c.recent_days })}</p>
        </Card>
      </div>

      <div className="patch-report__grid">
        <Card title={t('Le patch che tolgono più rischio')}>
          <DataTable
            rows={d.risk_reduction}
            rowKey={(p) => p.key}
            emptyText={t('Nessuna patch mancante.')}
            columns={[
              { key: 'name', header: t('Patch'), value: (p) => p.name, render: (p) => <span className="mono">{p.name}</span> },
              { key: 'severity', header: t('Gravità'), value: (p) => severityLabel(p.severity) },
              { key: 'priority', header: t('Rischio'), type: 'number', render: (p) => p.priority },
              { key: 'host_count', header: t('Macchine'), type: 'number', render: (p) => p.host_count },
              { key: 'weight', header: t('Rischio × macchine'), type: 'number', render: (p) => p.weight },
            ]}
          />
        </Card>
        <Card title={t('Cicli delle politiche di rilascio')}>
          <DataTable
            rows={d.policy_cycles}
            rowKey={(r) => `${r.policy}-${r.started_at}`}
            emptyText={t('Nessun ciclo nel periodo.')}
            columns={[
              { key: 'policy', header: t('Politica'), value: (r) => r.policy },
              { key: 'started', header: t('Iniziato'), type: 'date', value: (r) => r.started_at, render: (r) => formatDateTime(r.started_at) },
              { key: 'status', header: t('Stato'), value: (r) => runStatusLabel(r.status), filter: 'select',
                render: (r) => <span title={r.message ?? ''} className={chipClass(r.status === 'stopped' ? 'warn' : r.status === 'done' ? 'ok' : 'info')}>{runStatusLabel(r.status)}</span> },
              { key: 'rings', header: t('Ondate'), value: (r) => r.rings.map((a) => `${a.name}${a.success != null ? ` ${a.success}%` : ''}`).join(' → ') },
            ]}
          />
        </Card>
      </div>
    </>
  )
}
